import { Alert, AlertTitle, Box, Button, Checkbox, Dialog, DialogContent, FormControlLabel, Slide, Snackbar, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import MuiDialogHeader from '../Shared/MuiDialogHeader';
import { IsEmail } from '../Shared/IsEmail';
import InfoDialog from '../Shared/InfoDialog';
import { CreateUserSendSignupMailRequest, UsersGateApiClient } from '../Api/ApiServerHooks';
import { useGateApi } from '../Api/useGateApi';
import LanguagePicker from '../Shared/LanguagePicker';
import { useTranslation } from 'react-i18next';

interface CreateUserDialogProps {
    tenantId?: number;
    isOpen: boolean;
    emailDef?: string;
    onClose: (refresh: boolean) => void;
}

export default function CreateUserDialog(props: Readonly<CreateUserDialogProps>) {
    const { isOpen, onClose, emailDef, tenantId } = props;
    const { t } = useTranslation();
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [openCreateError, setOpenCreateError] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [msLogin, setMsLogin] = useState(false);
    const [localLogin, setLocalLogin] = useState(false);
    const [language, setLanguage] = useState<string>('cs');
    const [infoProps, setInfoProps] = useState<{ label: string, text: string }>();
    const { authorizedApi } = useGateApi(UsersGateApiClient);

    useEffect(() => {
        if (emailDef)
            setEmail(emailDef);
        else
            setEmail('');
        setName('');
        setSurname('');
        setButtonDisabled(false);
        setRefresh(false);
    }, []);

    useEffect(() => {
        if (emailDef)
            setEmail(emailDef);
        else
            setEmail('');
        setName('');
        setSurname('');
        setButtonDisabled(false);
        setRefresh(false);
        setMsLogin(true);
        setLocalLogin(false);
    }, [isOpen]);

    useEffect(() => {
        if (emailDef)
            setEmail(emailDef);
        else
            setEmail('');
    }, [emailDef]);

    useEffect(() => {
        if (msLogin)
            setLocalLogin(false);
    }, [msLogin]);

    useEffect(() => {
        if (localLogin)
            setMsLogin(false);
    }, [localLogin]);

    function TransitionLeft(props: any) {
        return <Slide {...props} direction='left' />;
    }

    const handleClose = (event: any) => {
        setOpenCreateError(false);
    }

    return (
        <Dialog onClose={() => onClose(refresh)} open={isOpen}>
            <MuiDialogHeader showCloseText title={t('Users.InsertUsers')} onClose={() => onClose(refresh)} />
            <DialogContent>
                <Box>
                    <Box>
                        <TextField
                            sx={{ margin: '12px', marginLeft: '24px', width: '350px' }}
                            label={t('Users.Name')}
                            size='small'
                            variant='outlined'
                            value={name}
                            onChange={(e) => { setName(e.target.value); }} />
                    </Box>
                    <Box>
                        <TextField
                            sx={{ margin: '12px', marginLeft: '24px', width: '350px' }}
                            label={t('Users.LastName')}
                            size='small'
                            variant='outlined'
                            value={surname}
                            onChange={(e) => { setSurname(e.target.value); }} />
                    </Box>
                    <Box>
                        <TextField
                            sx={{ margin: '12px', marginLeft: '24px', width: '350px' }}
                            label={t('Users.Email')}
                            size='small'
                            variant='outlined'
                            value={email}
                            disabled
                            onChange={(e) => { setEmail(e.target.value); }} />
                    </Box>
                    <Box>
                        <LanguagePicker notChangeLanguage showText onLanguageChange={(lang) => setLanguage(lang)}  />
                    </Box>
                    <Box /* style={{ display: 'flex', justifyContent: 'space-evenly'}} */>
                        <FormControlLabel
                            sx={{ marginLeft: '16px' }}
                            control={
                                <Checkbox checked={localLogin} onChange={(e) => setLocalLogin(e.target.checked)} />
                            }
                            label={t('Users.LocalLogin')}
                        />
                    </Box>
                    <Box>
                        <FormControlLabel
                            sx={{ marginLeft: '16px' }}
                            control={
                                <Checkbox checked={msLogin} onChange={(e) => setMsLogin(e.target.checked)} />
                            }
                            label={t('Users.LoginMS')}
                        />
                    </Box>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                        <Button variant='contained' sx={{ margin: 'auto' }} disabled={name.length === 0 || surname.length === 0 || email.length === 0 || !IsEmail(email) || buttonDisabled || (!localLogin && !msLogin)} onClick={() => {
                            setButtonDisabled(true);
                            authorizedApi?.createUserSendSignupMail(new CreateUserSendSignupMailRequest({
                                email: email,
                                localLogin: localLogin,
                                msLogin: msLogin,
                                tenantId: tenantId,
                                name: name,
                                surname: surname,
                                language: language
                            })).then((e) => {
                                if (e.ok) {
                                    setRefresh(true);
                                    setInfoProps({ label: 'Info', text: t('Users.InsertSuccess') });
                                    setInfoOpen(true);
                                }
                                else {
                                    setOpenCreateError(true);
                                }
                                setButtonDisabled(false);
                            });
                        }}>{t('Users.Insert')}</Button>
                    </Box>
                    {infoProps && <InfoDialog onClose={() => {
                        setInfoOpen(false);
                        onClose(true);
                    }} open={infoOpen} label={infoProps.label} text={infoProps.text} />}
                    <Box className="create-form-popup">
                        <Snackbar
                            TransitionComponent={TransitionLeft}
                            open={openCreateError}
                            autoHideDuration={3000}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: "top", horizontal: "right" }} >
                            <Alert onClose={handleClose} severity="warning">
                                <AlertTitle>{t('Administration.ProjectCreationError')}</AlertTitle>
                                {t('Users.UserExist')}
                            </Alert>
                        </Snackbar>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
